/**
 * Profile Service
 * Handles fetching and managing user profile data from Supabase
 * Includes retry logic and proper session handling
 */

import { supabase } from '../lib/supabase';

export interface UserProfile {
  user_roles: string[]; // Array-based roles (multi-role support)
  user_role?: string; // Legacy field (deprecated)
  founders_circle: boolean;
}

class ProfileService {
  /**
   * Get user profile with role and founders_circle status
   * Ensures session is active before querying
   * @param userId - User ID to fetch profile for
   * @param accessToken - Optional access token (if not provided, will fetch from session)
   */
  async getProfile(userId: string, accessToken?: string): Promise<{ data: UserProfile | null; error: any }> {
    try {
      console.log('[ProfileService] 🔍 Fetching profile for user:', userId);

      let token = accessToken;

      // If no token provided, try to get it from the session
      if (!token) {
        console.log('[ProfileService] ⏳ No token provided, fetching from session...');

        // Get current session from Supabase with timeout (prevents hanging)
        const sessionTimeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('getSession() timeout after 3 seconds')), 3000)
        );

        const { data: sessionData, error: sessionError } = await Promise.race([
          supabase.auth.getSession(),
          sessionTimeout
        ]) as any;

        if (sessionError || !sessionData.session) {
          console.error('[ProfileService] ❌ No active session:', sessionError?.message || 'Session is null');
          return {
            data: null,
            error: new Error('No active session')
          };
        }

        const session = sessionData.session;

        // Validate session token exists and hasn't expired
        if (!session.access_token) {
          console.error('[ProfileService] ❌ No access_token in session');
          return {
            data: null,
            error: new Error('Invalid session - no access token')
          };
        }

        if (session.expires_at && session.expires_at * 1000 < Date.now()) {
          console.error('[ProfileService] ❌ Session expired');
          return {
            data: null,
            error: new Error('Session expired')
          };
        }

        token = session.access_token;
      } else {
        console.log('[ProfileService] ✅ Using provided access token');
      }

      // WORKAROUND: Supabase JavaScript client .from().select() hangs indefinitely
      // Use direct fetch API instead, which works reliably
      console.log('[ProfileService] 🔧 Using direct fetch API (Supabase client hangs)');

      return await this.fetchWithRetry(() => this.fetchProfileDirect(userId, token!));
    } catch (error) {
      console.error('[ProfileService] ❌ Unexpected error:', error);
      return {
        data: null,
        error
      };
    }
  }

  /**
   * Fetch profile using direct REST API instead of Supabase client
   * This bypasses the hanging Supabase JavaScript client issue
   */
  private async fetchProfileDirect(
    userId: string,
    accessToken: string
  ): Promise<{ data: UserProfile | null; error: any }> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      // Direct PostgREST API call
      const url = `${supabaseUrl}/rest/v1/profiles?id=eq.${userId}&select=user_roles,founders_circle`;

      console.log('[ProfileService] 🌐 Fetching:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'apikey': anonKey,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ProfileService] ❌ HTTP error:', response.status, errorText);
        return {
          data: null,
          error: new Error(`HTTP ${response.status}: ${errorText}`)
        };
      }

      const data = await response.json();

      // PostgREST returns an array, even with filters
      if (!data || data.length === 0) {
        console.error('[ProfileService] ❌ No profile found');
        return {
          data: null,
          error: { code: 'PGRST116', message: 'Profile not found' }
        };
      }

      const profile = data[0];
      console.log('[ProfileService] ✅ Direct fetch successful:', profile);

      return {
        data: {
          user_roles: profile.user_roles,
          founders_circle: profile.founders_circle
        },
        error: null
      };
    } catch (error) {
      console.error('[ProfileService] ❌ Direct fetch error:', error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error(String(error))
      };
    }
  }

  /**
   * Retry logic for database queries
   * Implements exponential backoff
   */
  private async fetchWithRetry<T>(
    queryFn: () => Promise<{ data: T | null; error: any }>,
    maxRetries = 3
  ): Promise<{ data: T | null; error: any }> {
    for (let i = 0; i < maxRetries; i++) {
      console.log(`[ProfileService] 📡 Query attempt ${i + 1}/${maxRetries}...`);

      try {
        // Add timeout wrapper to prevent hanging forever
        const timeoutPromise = new Promise<{ data: null; error: any }>((_, reject) =>
          setTimeout(() => reject(new Error('Query timeout after 3 seconds')), 3000)
        );

        const result = await Promise.race([queryFn(), timeoutPromise]);

        // Success - return immediately
        if (!result.error) {
          console.log('[ProfileService] ✅ Query successful:', result.data);
          return result;
        }

        // Log the error
        console.error(`[ProfileService] ❌ Query attempt ${i + 1} failed:`, result.error);

        // If we have retries left, wait before trying again
        if (i < maxRetries - 1) {
          const waitMs = 1000 * (i + 1); // Exponential backoff: 1s, 2s, 3s
          console.log(`[ProfileService] ⏳ Retrying in ${waitMs}ms...`);
          await new Promise(resolve => setTimeout(resolve, waitMs));
        }
      } catch (error) {
        console.error(`[ProfileService] ❌ Query attempt ${i + 1} threw error:`, error);

        if (i < maxRetries - 1) {
          const waitMs = 1000 * (i + 1);
          console.log(`[ProfileService] ⏳ Retrying in ${waitMs}ms after error...`);
          await new Promise(resolve => setTimeout(resolve, waitMs));
        } else {
          // Last attempt failed
          return {
            data: null,
            error: error instanceof Error ? error : new Error(String(error))
          };
        }
      }
    }

    // All retries exhausted
    console.error('[ProfileService] ❌ All retries exhausted');
    return {
      data: null,
      error: new Error('All retry attempts failed')
    };
  }

  /**
   * Handle specific error types with appropriate actions
   */
  handleProfileError(error: any): string {
    if (!error) return 'user';

    console.error('[ProfileService] 🔍 Analyzing error:', error);

    // Row not found - profile doesn't exist
    if (error.code === 'PGRST116') {
      console.error('[ProfileService] ⚠️ Profile not found - may need to create profile');
      return 'user';
    }

    // JWT/Auth token issue
    if (error.message?.includes('JWT') || error.message?.includes('token')) {
      console.error('[ProfileService] 🔐 Auth token issue detected - user may need to re-authenticate');
      return 'user';
    }

    // RLS policy violation
    if (error.code === '42501' || error.message?.includes('policy')) {
      console.error('[ProfileService] 🔒 RLS policy violation - permission denied');
      return 'user';
    }

    // Network/timeout error
    if (error.message?.includes('fetch') || error.message?.includes('network')) {
      console.error('[ProfileService] 🌐 Network error - connection issue');
      return 'user';
    }

    // Unknown error - default to user role
    console.error('[ProfileService] ❓ Unknown error type - defaulting to user role');
    return 'user';
  }
}

// Export singleton instance
export const profileService = new ProfileService();
